import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MapPin, Star, Eye, Store, Trophy } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useCurrency } from '../../context/CurrencyContext';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Batch thumbnail fetcher — collects IDs and fetches in one request
const thumbCache = {};
let pendingIds = [];
let batchTimer = null;

const fetchThumbBatch = async () => {
  const ids = [...new Set(pendingIds)].filter(id => !thumbCache[id]);
  pendingIds = [];
  if (ids.length === 0) return;
  
  try {
    const res = await fetch(`${API_URL}/api/products/thumbs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids })
    });
    if (res.ok) {
      const data = await res.json();
      Object.entries(data.thumbs || {}).forEach(([id, url]) => {
        thumbCache[id] = url || '';
      });
    }
  } catch {}
  // Trigger re-renders for waiting components
  window.dispatchEvent(new CustomEvent('thumbs-loaded'));
};

const requestThumb = (productId) => {
  if (thumbCache[productId] !== undefined) return thumbCache[productId];
  pendingIds.push(productId);
  if (batchTimer) clearTimeout(batchTimer);
  batchTimer = setTimeout(fetchThumbBatch, 100); // Batch within 100ms
  return null; // Loading
};

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const { getUserByIdSync, toggleLike, user } = useApp();
  const { formatPriceWithOriginal, currency } = useCurrency();
  const seller = getUserByIdSync(product.seller_id || product.sellerId);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [thumbUrl, setThumbUrl] = useState(product.images?.[0] || '');
  
  const isLiked = user?.id && (product.liked_by || []).includes(user.id);
  const isFoundingSeller = product.is_founding_seller;
  const origPrice = product.original_price || product.originalPrice;
  const prevPrice = product.previous_price;
  
  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect fill='%23f0f7f4' width='400' height='400'/%3E%3Ctext x='200' y='210' text-anchor='middle' font-family='Georgia,serif' font-size='20' fill='%231a5038'%3EMinted%3C/text%3E%3C/svg%3E";

  // Batch-load thumbnail with direct fallback
  useEffect(() => {
    if (thumbUrl) return; // Already have an image
    
    const cached = requestThumb(product.id);
    if (cached !== null) {
      if (cached) setThumbUrl(cached);
      else {
        // Batch returned empty — try direct fetch as last resort
        fetch(`${API_URL}/api/products/${product.id}/thumb`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d?.image) setThumbUrl(d.image); })
          .catch(() => {});
      }
      return;
    }
    
    // Listen for batch completion
    const handler = () => {
      const url = thumbCache[product.id];
      if (url !== undefined) {
        if (url) {
          setThumbUrl(url);
        } else {
          // Batch returned empty — direct fallback
          fetch(`${API_URL}/api/products/${product.id}/thumb`)
            .then(r => r.ok ? r.json() : null)
            .then(d => { if (d?.image) setThumbUrl(d.image); })
            .catch(() => {});
        }
      }
    };
    window.addEventListener('thumbs-loaded', handler);
    return () => window.removeEventListener('thumbs-loaded', handler);
  }, [product.id, thumbUrl]);
  
  const getImageUrl = () => {
    if (!thumbUrl) return placeholderImage;
    if (thumbUrl.startsWith('/uploads/')) return placeholderImage;
    if (thumbUrl.includes('unsplash.com/photo-1523275335684')) return placeholderImage;
    if (thumbUrl.startsWith('http') || thumbUrl.startsWith('data:image')) return thumbUrl;
    // Handle raw base64 without data:image prefix
    if (thumbUrl.startsWith('/9j/') || thumbUrl.startsWith('iVBOR')) return `data:image/jpeg;base64,${thumbUrl}`;
    if (thumbUrl.length > 100) return `data:image/jpeg;base64,${thumbUrl}`;
    return placeholderImage;
  };

  // Handle image load error — try fixing wrong S3 bucket URL
  const handleImageError = () => {
    if (!imageError && thumbUrl && thumbUrl.includes('minted-marketplace-images')) {
      // Route through backend proxy
      try {
        const key = thumbUrl.split('minted-marketplace-images.s3.ap-southeast-2.amazonaws.com/')[1];
        if (key) {
          setThumbUrl(`${API_URL}/api/s3-proxy/${key}`);
          return;
        }
      } catch (e) {}
    }
    setImageError(true);
  };

  const isFeatured = product.is_featured;
  const isBoosted = product.is_boosted;
  const isFromStore = seller?.is_store;

  return (
    <div
      onClick={() => navigate(`/product/${product.id}`)}
      className="bg-white overflow-hidden cursor-pointer group relative rounded-2xl shadow-[0_2px_16px_rgba(26,58,42,0.04)] border border-[#1a3a2a]/5 hover:shadow-[0_8px_32px_rgba(26,58,42,0.08)] transition-shadow duration-500"
      data-testid={`product-card-${product.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/5] overflow-hidden bg-[#f0ede6]">
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-[#f0ede6] animate-pulse" />
        )}
        <img
          src={imageError ? placeholderImage : getImageUrl()}
          alt={product.title}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => handleImageError()}
          className={`w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out ${
            imageLoaded || imageError ? 'opacity-100' : 'opacity-0'
          }`}
        />
        
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        {/* Like button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleLike(product.id); }}
          className="absolute top-2.5 right-2.5 w-8 h-8 bg-white/80 backdrop-blur-md rounded-full flex items-center justify-center hover:scale-110 transition-transform shadow-sm"
          data-testid={`like-btn-${product.id}`}
        >
          <Heart className={`w-3.5 h-3.5 ${isLiked ? 'text-red-500 fill-red-500' : 'text-[#1a3a2a]'}`} strokeWidth={1.5} />
        </button>

        {/* Top-left badges */}
        <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5 pointer-events-none">
          {isFoundingSeller && (
            <span className="bg-[#1a3a2a]/85 backdrop-blur-md text-white text-[9px] font-semibold px-2 py-1 rounded-full tracking-[0.1em] uppercase flex items-center gap-1">
              <Trophy className="w-2.5 h-2.5 text-[#d4af37]" strokeWidth={2} />
              Founding
            </span>
          )}
          {isFeatured && (
            <span className="bg-[#d4af37]/90 backdrop-blur-md text-white text-[9px] font-semibold px-2 py-1 rounded-full tracking-[0.1em] uppercase flex items-center gap-1">
              <Star className="w-2.5 h-2.5" strokeWidth={2} />
              Featured
            </span>
          )}
        </div>
        
        {/* Bottom-left condition badge */}
        <div className="absolute bottom-2.5 left-2.5">
          <span className="bg-white/80 backdrop-blur-md text-[#1a3a2a] text-[9px] font-medium px-2.5 py-1 rounded-full tracking-[0.08em] uppercase">
            {product.condition}
          </span>
        </div>

        {/* Discount badge */}
        {origPrice && origPrice > product.price && (
          <div className="absolute bottom-2.5 right-2.5">
            <span className="bg-red-500/90 backdrop-blur-md text-white text-[9px] font-semibold px-2 py-1 rounded-full">
              -{Math.round(((origPrice - product.price) / origPrice) * 100)}%
            </span>
          </div>
        )}
        {/* Price drop badge */}
        {!origPrice && prevPrice && prevPrice > product.price && (
          <div className="absolute bottom-2.5 right-2.5">
            <span className="bg-[#d4af37]/90 backdrop-blur-md text-white text-[9px] font-semibold px-2 py-1 rounded-full flex items-center gap-0.5">
              Price Drop
            </span>
          </div>
        )}
      </div>

      {/* Details */}
      <div className="p-3.5">
        <h3 className="text-[#1a3a2a] text-[13px] font-medium line-clamp-2 leading-snug mb-2" style={{ fontFamily: 'Georgia, serif' }}>
          {product.title}
        </h3>
        
        <div className="flex items-center gap-1.5 text-[#a8a29e] text-[10px] mb-2.5">
          <MapPin className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
          <span className="truncate">{product.location}</span>
          {product.views > 0 && (
            <>
              <span className="text-[#e7e5e4]">·</span>
              <span>{product.views} views</span>
            </>
          )}
        </div>

        <div className="flex items-end justify-between">
          <div className="flex items-baseline gap-1.5 flex-wrap">
            {(() => {
              const p = formatPriceWithOriginal(product.price, product.listing_currency);
              return (
                <>
                  <span className="text-lg text-[#1a3a2a] tracking-tight" style={{ fontFamily: 'Georgia, serif' }}>{p.primary}</span>
                  {p.secondary && (
                    <span className="text-[10px] text-[#a8a29e]">{p.secondary}</span>
                  )}
                </>
              );
            })()}
            {origPrice && origPrice > product.price && currency === 'AUD' && (!product.listing_currency || product.listing_currency === 'AUD') && (
              <span className="text-[10px] text-[#a8a29e] line-through">${origPrice}</span>
            )}
            {!origPrice && prevPrice && prevPrice > product.price && currency === 'AUD' && (!product.listing_currency || product.listing_currency === 'AUD') && (
              <span className="text-[10px] text-[#d4af37]">was ${prevPrice}</span>
            )}
          </div>
          {seller && !seller.is_placeholder && (
            <div className="w-7 h-7 rounded-full overflow-hidden bg-[#1a3a2a] flex-shrink-0 ring-1 ring-[#1a3a2a]/10">
              {seller.avatar ? (
                <img src={seller.avatar} alt={seller.name} className="w-full h-full object-cover" title={seller.name} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white text-[10px] font-serif">
                  {seller.name?.charAt(0)?.toUpperCase()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
